import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { formatDate, prettify } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CopyBox } from "@/components/CopyBox";
import { Plus, Mail, Sparkles, Send, Download, Trash2, Eye, Loader2, Users } from "lucide-react";
import { toast } from "sonner";

const AUDIENCE = [
  { value: "all", label: "All subscribers" },
  { value: "lead", label: "From leads" },
  { value: "family", label: "From families" },
  { value: "partner", label: "From partners" },
  { value: "landing_page", label: "From landing pages" },
  { value: "public_signup", label: "Public signups" },
];

const renderHtml = (subject, body) => {
  const b = (body || "").replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br/>");
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/></head><body style="margin:0;background:#F4F8FB;font-family:Arial,Helvetica,sans-serif;color:#252221;"><div style="max-width:600px;margin:0 auto;background:#fff;"><div style="background:#1C4358;padding:20px 24px;color:#fff;font-size:18px;font-weight:bold;">MovingMyMama</div><div style="padding:24px;"><h1 style="color:#1C4358;font-size:22px;margin:0 0 12px;">${subject || ""}</h1><div style="font-size:15px;line-height:1.6;"><p>${b}</p></div></div><div style="padding:16px 24px;background:#F4F8FB;color:#6B7280;font-size:12px;">Your Trusted Resource For Senior Transitions · <a href="#" style="color:#9C1E22;">Unsubscribe</a></div></div></body></html>`;
};

export default function Newsletters() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [preview, setPreview] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [ai, setAi] = useState({ topic: "", tone: "warm and professional" });
  const [aiBusy, setAiBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/marketing/newsletters"); setItems(data.items || []); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = async () => {
    try {
      const { data } = await api.post("/marketing/newsletters", { title: "Untitled Newsletter", subject: "", body_md: "", audience: { source: "all" } });
      setEditor({ ...data, audience: data.audience || { source: "all" } });
      load();
    } catch { toast.error("Could not create newsletter"); }
  };
  const openEdit = (n) => setEditor({ ...n, audience: n.audience || { source: "all" } });

  const saveEditor = async (silent) => {
    if (!editor) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/marketing/newsletters/${editor.id}`, {
        title: editor.title, subject: editor.subject, body_md: editor.body_md, audience: editor.audience,
      });
      setEditor({ ...editor, ...data });
      if (!silent) toast.success("Saved");
      load();
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const runAi = async () => {
    if (!ai.topic.trim()) return toast.error("Add a topic to draft from");
    setAiBusy(true);
    try {
      const { data } = await api.post("/marketing/newsletters/ai-draft", ai);
      setEditor((e) => ({ ...e, subject: data.subject || e.subject, body_md: data.body || e.body_md }));
      setAiOpen(false);
      toast.success("Draft inserted — review & edit before sending");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI assist failed"); }
    finally { setAiBusy(false); }
  };

  const send = async () => {
    await saveEditor(true);
    try {
      const { data } = await api.post(`/marketing/newsletters/${editor.id}/send`);
      toast.success(data.message || "Queued");
      setEditor(null); load();
    } catch { toast.error("Could not send"); }
  };

  const exportRecipients = async () => {
    try {
      const res = await api.get(`/marketing/newsletters/${editor.id}/export`, { responseType: "blob" });
      const u = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = u; a.download = "newsletter_recipients.csv"; a.click(); URL.revokeObjectURL(u);
    } catch { toast.error("Export failed"); }
  };

  const remove = async (n) => { await api.delete(`/marketing/newsletters/${n.id}`); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-5" data-testid="marketing-newsletters">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#252221]">Newsletter</h1>
          <p className="text-sm text-muted-foreground">Draft with AI and manage your audience. Email delivery arrives in a later phase — export or copy HTML to send for now.</p>
        </div>
        <Button onClick={openNew} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="new-newsletter-button"><Plus size={15} className="mr-1.5" /> New newsletter</Button>
      </div>

      {loading ? <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div> :
        items.length === 0 ? (
          <Card className="p-12 text-center border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="newsletters-empty">
            <Mail size={26} className="mx-auto text-[#1C4358] mb-3" />
            <p className="font-medium text-[#252221]">No newsletters yet</p>
            <p className="text-sm text-muted-foreground">Create your first newsletter and let AI help you draft it.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map((n) => (
              <Card key={n.id} className="p-4 border-[#E6EDF2] flex items-center gap-3" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="newsletter-card">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(183,121,31,0.12)" }}><Mail size={18} className="text-[#B7791F]" /></div>
                <div className="min-w-0 flex-1 cursor-pointer" onClick={() => openEdit(n)}>
                  <div className="font-medium text-[#252221] truncate">{n.title}</div>
                  <div className="text-xs text-muted-foreground truncate">{n.subject || "No subject yet"}{n.status === "sent" ? ` · ${n.recipient_count} recipients` : ""}</div>
                </div>
                <StatusBadge value={n.status} />
                <Button size="sm" variant="outline" onClick={() => openEdit(n)} data-testid="newsletter-open">Open</Button>
                <button onClick={() => remove(n)} className="p-2 rounded-lg text-[#9C1E22] hover:bg-[rgba(156,30,34,0.08)]" data-testid="newsletter-delete" aria-label="Delete"><Trash2 size={15} /></button>
              </Card>
            ))}
          </div>
        )}

      {/* Composer */}
      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto mmm-scroll" data-testid="newsletter-composer">
          <DialogHeader><DialogTitle>Compose newsletter</DialogTitle></DialogHeader>
          {editor && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="space-y-3">
                <div><Label className="text-sm">Title (internal)</Label><Input value={editor.title} onChange={(e) => setEditor({ ...editor, title: e.target.value })} data-testid="newsletter-title" /></div>
                <div><Label className="text-sm">Subject line</Label><Input value={editor.subject} onChange={(e) => setEditor({ ...editor, subject: e.target.value })} placeholder="News from MovingMyMama" data-testid="newsletter-subject" /></div>
                <div>
                  <div className="flex items-center justify-between mb-1"><Label className="text-sm">Body</Label>
                    <Button size="sm" variant="outline" onClick={() => setAiOpen(true)} data-testid="newsletter-ai-button"><Sparkles size={14} className="mr-1.5 text-[#B7791F]" /> AI draft</Button>
                  </div>
                  <Textarea rows={10} value={editor.body_md} onChange={(e) => setEditor({ ...editor, body_md: e.target.value })} placeholder="Write your newsletter, or use AI draft to get started..." data-testid="newsletter-body" className="resize-none" />
                </div>
                <div><Label className="text-sm">Audience</Label>
                  <Select value={editor.audience?.source || "all"} onValueChange={(v) => setEditor({ ...editor, audience: { ...editor.audience, source: v } })}>
                    <SelectTrigger data-testid="newsletter-audience"><SelectValue /></SelectTrigger>
                    <SelectContent>{AUDIENCE.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-sm mb-1.5 block">Live preview</Label>
                <div className="rounded-xl border border-[#E6EDF2] overflow-hidden bg-white" style={{ height: 360 }}>
                  <iframe title="preview" srcDoc={renderHtml(editor.subject, editor.body_md)} className="w-full h-full" data-testid="newsletter-preview-frame" />
                </div>
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#F0F4F7] mt-2">
            <Button variant="outline" onClick={() => saveEditor(false)} disabled={saving} data-testid="newsletter-save">{saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null} Save draft</Button>
            <Button variant="outline" onClick={() => setPreview(true)} data-testid="newsletter-copy-html"><Eye size={15} className="mr-1.5" /> Get HTML</Button>
            <Button variant="outline" onClick={exportRecipients} data-testid="newsletter-export"><Download size={15} className="mr-1.5" /> Export recipients</Button>
            <Button onClick={send} className="text-white ml-auto" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="newsletter-send"><Send size={15} className="mr-1.5" /> Mark as sent</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI dialog */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="sm:max-w-md" data-testid="newsletter-ai-dialog">
          <DialogHeader><DialogTitle>Draft with AI</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-sm">What's this newsletter about?</Label><Textarea rows={3} value={ai.topic} onChange={(e) => setAi({ ...ai, topic: e.target.value })} placeholder="e.g. Tips for downsizing a parent's home with compassion" data-testid="ai-topic" /></div>
            <div><Label className="text-sm">Tone</Label><Input value={ai.tone} onChange={(e) => setAi({ ...ai, tone: e.target.value })} data-testid="ai-tone" /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2"><Button variant="outline" onClick={() => setAiOpen(false)}>Cancel</Button><Button onClick={runAi} disabled={aiBusy} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="ai-generate">{aiBusy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Sparkles size={15} className="mr-1.5" />} Generate</Button></div>
        </DialogContent>
      </Dialog>

      {/* HTML copy dialog */}
      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="sm:max-w-2xl" data-testid="newsletter-html-dialog">
          <DialogHeader><DialogTitle>Newsletter HTML</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Copy this HTML into your email tool to send.</p>
          <CopyBox value={editor ? renderHtml(editor.subject, editor.body_md) : ""} testid="newsletter-html-copy" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
